import countries, { formatPopulation } from './countries';
import animalCountryStats from './higherOrLowerAnimals';

export const MIXED_METRIC_BIAS = 0.55;
export const ANIMAL_MIX_BIAS = 0.55;

export const formatArea = (n) => Math.round(n).toLocaleString('en-US');

export const getMetricLabel = (entry) => {
  if (entry.metric === 'area') return 'Territory size';
  if (entry.metric === 'animals') return entry.animal || 'Animals';
  return 'Population';
};

export const getMetricPromptWord = (entry) => {
  if (entry.metric === 'area') return 'territory size';
  if (entry.metric === 'animals') return (entry.animal || 'animal count').toLowerCase();
  return 'population';
};

export const toNumericValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value == null) return NaN;
  const parsed = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : NaN;
};

export const getEntryValue = (entry) => toNumericValue(entry?.value);

export const getEntryKey = (entry) => entry.key;

export const formatEntryValue = (entry, value) => {
  const n = toNumericValue(value);
  if (entry.metric === 'area') return formatArea(n);
  return formatPopulation(Math.round(n));
};

const toAnimalPoolEntry = (stat) => ({
  key: stat.key,
  sourceKind: 'animal',
  metric: 'animals',
  animal: stat.animal,
  name: `${stat.animal} in ${stat.place || stat.countryName}`,
  countryName: stat.countryName,
  code: stat.code,
  value: toNumericValue(stat.value),
});

export const buildComparisonPool = () => {
  const entries = [];

  countries.forEach((country) => {
    entries.push({
      key: `${country.code}-population`,
      sourceKind: 'country',
      metric: 'population',
      name: country.name,
      code: country.code,
      value: toNumericValue(country.population),
    });

    const area = toNumericValue(country.area);
    if (Number.isFinite(area) && area > 0) {
      entries.push({
        key: `${country.code}-area`,
        sourceKind: 'country',
        metric: 'area',
        name: country.name,
        code: country.code,
        value: area,
      });
    }
  });

  animalCountryStats.forEach((stat) => {
    const entry = toAnimalPoolEntry(stat);
    if (Number.isFinite(entry.value) && entry.value > 0) {
      entries.push(entry);
    }
  });

  return entries;
};

export const pickFromPool = (pool, excludeKeys = []) => {
  const exclude = new Set(excludeKeys);
  const available = pool.filter((entry) => !exclude.has(entry.key));
  const source = available.length ? available : pool;
  return source[Math.floor(Math.random() * source.length)];
};

const pickRandom = (list) => list[Math.floor(Math.random() * list.length)];

const isAnimalEntry = (entry) => entry.sourceKind === 'animal';

export const pickPartner = (pool, current, extraExclude = []) => {
  const exclude = new Set([current.key, ...extraExclude]);
  const currentValue = getEntryValue(current);
  const eligible = (entry) =>
    !exclude.has(entry.key) && getEntryValue(entry) !== currentValue;

  const candidates = pool.filter(eligible);
  if (!candidates.length) return pickFromPool(pool, [...exclude]);

  const oppositeMetric = candidates.filter((entry) => entry.metric !== current.metric);
  const sameMetric = candidates.filter((entry) => entry.metric === current.metric);
  const useMixed = oppositeMetric.length > 0 && Math.random() < MIXED_METRIC_BIAS;

  if (useMixed) {
    if (!isAnimalEntry(current)) {
      const mixedAnimals = oppositeMetric.filter(isAnimalEntry);
      if (mixedAnimals.length > 0 && Math.random() < ANIMAL_MIX_BIAS) {
        return pickRandom(mixedAnimals);
      }
    }
    return pickRandom(oppositeMetric);
  }

  if (sameMetric.length) return pickRandom(sameMetric);
  return pickRandom(candidates);
};

export const pickComparisonPair = (pool) => {
  const left = pickFromPool(pool);
  let right = pickPartner(pool, left);
  let guard = 0;
  while (getEntryValue(right) === getEntryValue(left) && guard < 20) {
    right = pickFromPool(pool, [left.key, right.key]);
    guard += 1;
  }
  return { left, right };
};

export const pickNextComparison = (pool, current, extraExclude = []) => {
  const exclude = [current.key, ...extraExclude];
  const currentValue = getEntryValue(current);
  let next = pickPartner(pool, current, extraExclude);
  let guard = 0;
  while (getEntryValue(next) === currentValue && guard < 20) {
    next = pickFromPool(pool, [...exclude, next.key]);
    guard += 1;
  }
  return next;
};
